import lodashUniq from 'lodash-es/uniq';

import type { StoryblokAlignmentDataSource, StoryblokUiColorsDataSource } from '~storyblok/types/fleequid/storyblok-components';

export type UseStoryblokComponentClassReturnType = {
    classes: ComputedRef<string[]>;
};

export type StoryblokClassFields = {
    maxWidth?: StoryblokSizingDataSource;
    rounded?: StoryblokBorderRadiusDataSource;
    color?: StoryblokUiColorsDataSource;
    anchorId?: string;
    center?: boolean;
    align?: StoryblokAlignmentDataSource;
    aspectRatio?: StoryblokAspectRatioDataSource;
    fontSize?: StoryblokFontSizesDataSource;
    duration?: StoryblokDurationDataSource;
};

/**
 * Tailwind CSS scans source files for complete class strings at build time.
 * Dynamically constructed classes (e.g. `rounded-${value}`) are NOT detected.
 * These maps ensure every possible class string is visible to Tailwind's scanner.
 */
const ROUNDED_CLASS_MAP: Record<StoryblokBorderRadiusDataSource, string> = {
    xs: 'rounded-xs',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    '4xl': 'rounded-4xl',
    none: 'rounded-none',
    full: 'rounded-full',
};

const MAX_WIDTH_CLASS_MAP: Record<StoryblokSizingDataSource, string> = {
    '3xs': 'max-w-3xs',
    '2xs': 'max-w-2xs',
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    full: 'max-w-full',
};

const COLOR_CLASS_MAP: Record<StoryblokUiColorsDataSource, string> = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    neutral: 'text-neutral',
    success: 'text-success',
    info: 'text-info',
    warning: 'text-warning',
    error: 'text-error',
};

const ALIGN_CLASS_MAP: Record<StoryblokAlignmentDataSource, string> = {
    start: 'items-start',
    end: 'items-end',
    center: 'items-center',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
};

const ASPECT_RATIO_CLASS_MAP: Record<StoryblokAspectRatioDataSource, string> = {
    auto: 'aspect-auto',
    '1/1': 'aspect-[1/1] object-cover',
    '4/3': 'aspect-[4/3] object-cover',
    '16/9': 'aspect-[16/9] object-cover',
};

const FONT_SIZES_CLASS_MAP: Record<StoryblokFontSizesDataSource, string> = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
};

const DURATION_CLASS_MAP: Record<StoryblokDurationDataSource, string> = {
    '20': '[--duration:20s]',
    '30': '[--duration:30s]',
    '40': '[--duration:40s]',
    '50': '[--duration:50s]',
};

export const useStoryblokComponentClass = (fields: MaybeRefOrGetter<StoryblokClassFields>): UseStoryblokComponentClassReturnType => {
    return {
        classes: computed(() => {
            const f = toValue(fields);
            const roundedClass = f?.rounded ? ROUNDED_CLASS_MAP[f.rounded] : undefined;
            const maxWidthClass = f?.maxWidth ? MAX_WIDTH_CLASS_MAP[f.maxWidth] : undefined;
            const colorClass = f?.color ? COLOR_CLASS_MAP[f.color] : undefined;
            const alignClass = f?.align ? ALIGN_CLASS_MAP[f.align] : undefined;
            const aspectRatioClass = f?.aspectRatio ? ASPECT_RATIO_CLASS_MAP[f.aspectRatio] : undefined;
            const fontSizeClass = f?.fontSize ? FONT_SIZES_CLASS_MAP[f.fontSize] : undefined;
            const durationClass = f?.duration ? DURATION_CLASS_MAP[f.duration] : undefined;

            return lodashUniq([
                ...(roundedClass ? [roundedClass] : []),
                ...(maxWidthClass ? [maxWidthClass, 'mx-auto'] : []),
                ...(colorClass ? [colorClass] : []),
                ...(f?.anchorId ? ['scroll-mt-[var(--header-height)]'] : []),
                ...(f?.center ? ['text-center'] : []),
                ...(alignClass ? ['flex', 'flex-col', alignClass] : []),
                ...(aspectRatioClass ? [aspectRatioClass] : []),
                ...(fontSizeClass ? [fontSizeClass] : []),
                ...(durationClass ? [durationClass] : []),
            ]);
        }),
    };
};
